// What the widget picker offers for one device (VisionPlatform device-dashboards DDB-11 to DDB-13, WEB-DDB-PICK):
// the type's suggestions first, then every widget type the device can fill. Pure, so edit mode's picker dialog and
// its tests share it.
import type { CreavisionDashboardDocument } from "../../../documents/generated/dashboard";
import type { WidgetPage } from "./queries";
import { suggestedProfileWidgets, type PageDocument } from "./profile";

/** A widget in the profile's shape: a type and the device's keys (device-profile.json `widget`). */
export interface Suggestion {
  type: string;
  datapoints?: string[];
  commands?: string[];
  settings?: string[];
  title?: string;
  config?: Record<string, unknown>;
}

type PageWidget = CreavisionDashboardDocument["widgets"][number];

const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

/** A profile widget (`ui.widgets` entry) as a suggestion; anything without a type is dropped. */
function fromProfile(raw: Record<string, unknown>): Suggestion | undefined {
  if (typeof raw.type !== "string") return undefined;
  const s: Suggestion = { type: raw.type };
  for (const k of ["datapoints", "commands", "settings"] as const) {
    const keys = strings(raw[k]);
    if (keys.length) s[k] = keys;
  }
  if (typeof raw.title === "string") s.title = raw.title;
  if (isObject(raw.config)) s.config = raw.config;
  return s;
}

/** A device-page widget as a suggestion: its bindings' keys, in the profile's shape. */
export function fromPageWidget(w: PageWidget): Suggestion {
  const s: Suggestion = { type: w.type };
  const pick = (k: "datapoint" | "command" | "setting") =>
    (w.bindings ?? []).flatMap((b) => (typeof b[k] === "string" ? [b[k]] : []));
  const datapoints = pick("datapoint");
  const commands = pick("command");
  const settings = pick("setting");
  if (datapoints.length) s.datapoints = datapoints;
  if (commands.length) s.commands = commands;
  if (settings.length) s.settings = settings;
  if (typeof w.title === "string") s.title = w.title;
  if (isObject(w.config) && Object.keys(w.config).length) s.config = w.config;
  return s;
}

/** Two widgets show the same thing when their type and bound keys match; titles and config do not count. */
export function sameWidget(a: Suggestion, b: Suggestion): boolean {
  const keys = (s: Suggestion) =>
    JSON.stringify([
      s.type,
      [...(s.datapoints ?? [])].sort(),
      [...(s.commands ?? [])].sort(),
      [...(s.settings ?? [])].sort(),
    ]);
  return keys(a) === keys(b);
}

type PointInfo = WidgetPage["points"][string];

/**
 * The device type lets a widget of this type show the value (device-type-console DTC-20, DTC-21): the point's
 * `widgets` lists the allowed types; without it every type that can show the value may.
 */
export function allows(point: PointInfo | undefined, type: string): boolean {
  return !point?.widgets || point.widgets.includes(type);
}

/** Every key a suggestion names is on the device (its page has the point or action), and its type may show them. */
function fits(s: Suggestion, page: WidgetPage): boolean {
  return (
    (s.datapoints ?? []).every((k) => k in page.points && allows(page.points[k], s.type)) &&
    [...(s.commands ?? []), ...(s.settings ?? [])].every((k) => k in page.actions)
  );
}

/**
 * "Suggested for <device type>" (DDB-11, DDB-13): `ui.widgets`, then the default page's widgets not on the page
 * being edited; without `ui.widgets`, the default page's widgets plus one `value` per numeric datapoint not already
 * on it. A suggestion naming a key the unit lacks, or of a type it cannot fill, is left out, and none repeats.
 */
export function suggestions(
  doc: PageDocument | undefined,
  defaults: CreavisionDashboardDocument | undefined,
  page: WidgetPage,
  editing: Suggestion[],
): Suggestion[] {
  const listed = suggestedProfileWidgets(doc)?.flatMap((w) => fromProfile(w) ?? []);
  const fromDefault = (defaults?.widgets ?? []).map(fromPageWidget);
  const fillable: readonly string[] = fillableTypes(doc, page);
  const out: Suggestion[] = [];
  const add = (s: Suggestion) => {
    if (fillable.includes(s.type) && fits(s, page) && !out.some((o) => sameWidget(o, s))) out.push(s);
  };
  if (listed) {
    listed.forEach(add);
    fromDefault.filter((s) => !editing.some((e) => sameWidget(e, s))).forEach(add);
  } else {
    fromDefault.forEach(add);
    const shown = new Set([...fromDefault, ...editing].flatMap((s) => s.datapoints ?? []));
    for (const [k, p] of Object.entries(page.points)) {
      if (p.kind === "number" && !shown.has(k)) add({ type: "value", datapoints: [k] });
    }
  }
  return out;
}

/** The widget types this app draws on a device page, in the order the picker lists them. */
const PICKABLE = [
  "value",
  "value_grid",
  "gauge",
  "line_chart",
  "bar_chart",
  "toggle",
  "slider",
  "compass",
  "map",
  "status_list",
  "alarm_list",
  "device_tile",
  "level",
  "sparkline",
  "stats",
  "state_timeline",
  "heatmap",
  "health",
  "period_totals",
  "period_compare",
  "year_compare",
  "weather_station",
  "reset_counter",
] as const;
export type PickableType = (typeof PICKABLE)[number];

/**
 * The Pro widgets: the statistics widgets (pro-statistics §2) and the weather station widgets (weather-station-widgets
 * WSW-20) need the device's `statistics` feature (PST-01, PST-03).
 */
export const STATISTICS_TYPES: readonly string[] = [
  "period_totals",
  "period_compare",
  "year_compare",
  "weather_station",
  "reset_counter",
];

/**
 * PST-10: a number with a unit whose profile `aggregate` is sum fits the three statistics widgets; one averaged fits
 * the comparison and the year chart only.
 */
export function statisticsFit(doc: PageDocument | undefined, key: string, p: PointInfo, type: string): boolean {
  if (p.kind !== "number" || !p.unit || !allows(p, type)) return false;
  const agg = doc?.datapoints?.find((d) => d.key === key)?.aggregate;
  return agg === "sum" || (agg === "avg" && type !== "period_totals");
}

/** WSW-10: a counter adds up only a value whose readings are summed. */
function counterFit(doc: PageDocument | undefined, key: string): boolean {
  return doc?.datapoints?.find((d) => d.key === key)?.aggregate === "sum";
}

const MAP_TRAITS = new Set(["gnss_tracker", "location"]);
/** What a device tile shows (device-type-pages DTP-10): numbers, on/off and enum readings. */
const TILE_KINDS = new Set(["number", "boolean", "enum"]);

/**
 * The widget types the device can fill (DDB-12, WEB-DDB-PICK-02): the picker never offers one that would stay empty.
 * Types the app draws as "not yet", and scene_button (it runs an automation, not a device), are never offered here.
 */
export function fillableTypes(doc: PageDocument | undefined, page: WidgetPage): PickableType[] {
  const points = Object.entries(page.points);
  // A number this type may show: the device type can narrow a value's widgets (DTC-21).
  const numeric = (type: PickableType) => points.some(([, p]) => p.kind === "number" && allows(p, type));
  const boolKeys = new Set(points.filter(([, p]) => p.kind === "boolean").map(([k]) => k));
  const settings = (doc?.settings ?? []).filter((s) => typeof s.key === "string" && s.key in page.actions);
  const commands = (doc?.commands ?? []).filter((c) => typeof c.key === "string" && c.key in page.actions);
  const can: Record<PickableType, boolean> = {
    value: numeric("value"),
    value_grid: numeric("value_grid"),
    gauge: numeric("gauge"),
    line_chart: numeric("line_chart"),
    bar_chart: numeric("bar_chart"),
    toggle:
      settings.some((s) => typeof s.default === "boolean") ||
      commands.some((c) => typeof c.reports === "string" && boolKeys.has(c.reports)),
    slider: settings.some((s) => typeof s.min === "number" && typeof s.max === "number"),
    compass: points.some(([, p]) => p.kind === "number" && p.unit === "°" && allows(p, "compass")),
    map:
      points.some(([, p]) => p.kind === "position") ||
      (doc?.traits ?? []).some((t) => MAP_TRAITS.has(t)) ||
      !!page.device.position,
    status_list: true,
    alarm_list: true,
    device_tile: points.some(([, p]) => TILE_KINDS.has(p.kind)),
    level: numeric("level"),
    sparkline: numeric("sparkline"),
    stats: numeric("stats"),
    state_timeline: points.some(([, p]) => p.kind === "boolean" && allows(p, "state_timeline")),
    heatmap: numeric("heatmap"),
    health: true,
    period_totals: points.some(([k, p]) => statisticsFit(doc, k, p, "period_totals")),
    period_compare: points.some(([k, p]) => statisticsFit(doc, k, p, "period_compare")),
    year_compare: points.some(([k, p]) => statisticsFit(doc, k, p, "year_compare")),
    // A station needs its large value, a temperature (WSW-03); a counter a summed value (WSW-10).
    weather_station: points.some(([, p]) => p.station_slot === "temperature"),
    reset_counter: points.some(([k, p]) => statisticsFit(doc, k, p, "reset_counter") && counterFit(doc, k)),
  };
  return PICKABLE.filter((t) => can[t]);
}

/** How many keys a widget type binds when the picker builds it from a bare type (dashboard.json). */
export function keyLimit(type: PickableType): number {
  if (type === "value_grid") return 12;
  if (type === "line_chart" || type === "bar_chart") return 8;
  if (type === "compass") return 3;
  if (type === "device_tile") return 4;
  if (type === "level" || type === "state_timeline") return 6;
  if (type === "map" || type === "status_list" || type === "alarm_list" || type === "health") return 0;
  if (type === "weather_station") return 0;
  return 1;
}

/**
 * The keys a bare widget type can bind on this device, with their names: numeric datapoints the type may show for
 * values, gauges and charts (DTC-21); readings for a compass; boolean settings or commands reporting a boolean for a toggle; settings with
 * limits for a slider. Source widgets bind none.
 */
export function keysFor(
  type: PickableType,
  doc: PageDocument | undefined,
  page: WidgetPage,
): { key: string; name: string; kind: "datapoint" | "command" | "setting" }[] {
  const points = Object.entries(page.points);
  const actionName = (k: string) => page.actions[k]?.name ?? k;
  const boolKeys = new Set(points.filter(([, p]) => p.kind === "boolean").map(([k]) => k));
  switch (type) {
    case "value":
    case "value_grid":
    case "gauge":
    case "line_chart":
    case "bar_chart":
    case "compass":
    case "level":
    case "sparkline":
    case "stats":
    case "heatmap":
      return points
        .filter(([, p]) => p.kind === "number" && allows(p, type))
        .map(([key, p]) => ({ key, name: p.name, kind: "datapoint" }));
    case "toggle":
      return [
        ...(doc?.settings ?? [])
          .filter((s) => typeof s.key === "string" && s.key in page.actions && typeof s.default === "boolean")
          .map((s) => ({ key: s.key as string, name: actionName(s.key as string), kind: "setting" as const })),
        ...(doc?.commands ?? [])
          .filter(
            (c) =>
              typeof c.key === "string" &&
              c.key in page.actions &&
              typeof c.reports === "string" &&
              boolKeys.has(c.reports),
          )
          .map((c) => ({ key: c.key as string, name: actionName(c.key as string), kind: "command" as const })),
      ];
    case "state_timeline":
      return points
        .filter(([, p]) => p.kind === "boolean" && allows(p, type))
        .map(([key, p]) => ({ key, name: p.name, kind: "datapoint" }));
    case "period_totals":
    case "period_compare":
    case "year_compare":
      return points
        .filter(([key, p]) => statisticsFit(doc, key, p, type))
        .map(([key, p]) => ({ key, name: p.name, kind: "datapoint" }));
    case "reset_counter":
      return points
        .filter(([key, p]) => statisticsFit(doc, key, p, type) && counterFit(doc, key))
        .map(([key, p]) => ({ key, name: p.name, kind: "datapoint" }));
    case "device_tile":
      return points
        .filter(([, p]) => TILE_KINDS.has(p.kind))
        .map(([key, p]) => ({ key, name: p.name, kind: "datapoint" }));
    case "slider":
      return (doc?.settings ?? [])
        .filter(
          (s) =>
            typeof s.key === "string" &&
            s.key in page.actions &&
            typeof s.min === "number" &&
            typeof s.max === "number",
        )
        .map((s) => ({ key: s.key as string, name: actionName(s.key as string), kind: "setting" as const }));
    default:
      return [];
  }
}

const HOURLY = new Set(["avg", "min", "max", "sum"]);

/**
 * A suggestion built from a bare type and the keys the person chose. A heatmap sums its hours like the datapoint does
 * (energy per hour), else averages them (WLB-14); the profile says which.
 */
export function fromChoice(
  type: PickableType,
  keys: { key: string; kind: "datapoint" | "command" | "setting" }[],
  doc?: PageDocument,
): Suggestion {
  const pick = (k: "datapoint" | "command" | "setting") => keys.filter((x) => x.kind === k).map((x) => x.key);
  const s: Suggestion = { type };
  if (type === "heatmap") {
    const agg = doc?.datapoints?.find((d) => d.key === pick("datapoint")[0])?.aggregate;
    if (typeof agg === "string" && agg !== "avg" && HOURLY.has(agg)) s.config = { aggregate: agg };
  }
  if (pick("datapoint").length) s.datapoints = pick("datapoint");
  if (pick("command").length) s.commands = pick("command");
  if (pick("setting").length) s.settings = pick("setting");
  return s;
}
